import { useState } from "react";

import axios from "axios";

import { CertificateCard } from "entities/certificate";
import { Provider } from "entities/provider";
import { Avatar } from "entities/provider/ui/avatar";
import { EncryptedZkCert } from "shared/snap";
import { useGetSnapQuery, useInvokeSnapMutation } from "shared/snap/rq";
import { Button } from "shared/ui/button";
import { Icon } from "shared/ui/icon";
import { capitalizeFirstLetter } from "shared/utils";
import { downloadObjectAsJson } from "shared/utils/download-object-as-json";

type Props = {
  certificate: string;
  from: string;
  provider: Provider;
  onError: (error: unknown) => void;
};
export const GenerationSuccessContent = ({
  certificate,
  from,
  provider,
  onError,
}: Props) => {
  const [isDownloaded, setIsDownloaded] = useState(false);

  const snapQuery = useGetSnapQuery();
  const mutation = useInvokeSnapMutation("importZkCert");

  const downloadFn = async () => {
    const url = `${import.meta.env.VITE_API_HOST}/download/${certificate}`;
    try {
      const res = await axios.get(url);
      const data = res.data;

      downloadObjectAsJson(
        JSON.stringify(data),
        `${capitalizeFirstLetter(provider)}-zkCert.json`
      );
    } catch (err) {
      console.log("Request certificate error: ", err);
    }
  };

  const handleGet = async () => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_HOST}/download/${certificate}`
      );
      const data: EncryptedZkCert = await res.json();

      console.log("EncryptedZkCert:", data);
      await mutation.mutateAsync({
        chainID: parseInt(import.meta.env.VITE_AUTH_URL),
        encryptedZkCert: data,
      });
      setIsDownloaded(true);
    } catch (error) {
      console.error(error);
      onError(error);
    }
  };

  const downloadHandler = () => {
    downloadFn();
    setIsDownloaded(true);
  };

  const uploadGalaClick = () => {
    const url = from || "https://app.galactica.com/my-certificates";

    window.open(url, "_blank", "noopener,noreferrer");
  };

  let card = null;

  switch (provider) {
    case "twitter":
      card = (
        <CertificateCard
          avatar={<Avatar onlyImage provider="twitter" />}
          className="h-[186px] border-0 bg-gradient-to-tr from-[#5D5D5D] to-[#1B1B1B]"
          issueDate={null}
          title="X.com (Twitter) zkCertificate"
        />
      );
      break;
    case "uniswap":
      card = (
        <CertificateCard
          avatar={<Avatar onlyImage provider="uniswap" />}
          className="h-[186px] border-0 bg-gradient-to-tr from-[#ffe8f7] to-[#f538d0] text-black/60"
          issueDate={null}
          title="Uniswap zkCertificate"
        />
      );
      break;
  }

  return (
    <>
      <header className="mb-5 flex flex-col items-center justify-center">
        <Icon className="h-6 w-9" name="occam" />
        <h3 className="mt-4 text-center text-lg font-medium">
          {provider === "twitter" ? "The X.com (Twitter)" : "Uniswap"}{" "}
          Certificate was successfully generated
        </h3>
      </header>
      {card}
      <footer className="mt-5 flex flex-col gap-2 font-medium">
        {!isDownloaded && !snapQuery.data && (
          <Button
            className="flex h-11 items-center justify-center gap-1 text-base font-medium"
            onClick={downloadHandler}
          >
            <Icon name="fileDownload" />
            Download
          </Button>
        )}

        {!isDownloaded && snapQuery.data && (
          <Button
            isLoading={mutation.isPending}
            className="flex h-11 items-center justify-center gap-1 text-base font-medium"
            onClick={handleGet}
          >
            <Icon name="fileDownload" />
            Get Certificate
          </Button>
        )}

        {isDownloaded && (
          <>
            <Button className="flex h-11" onClick={uploadGalaClick}>
              {snapQuery.data ? "Back to Galactica" : "Upload to Wallet"}
            </Button>
            <div className="flex justify-center">
              <div className="flex items-center space-x-2">
                <Icon
                  name="checkCircle"
                  svgClassName="stroke-current text-black/50"
                />

                <span className="text-black">Certificate downloaded</span>
              </div>
            </div>
          </>
        )}
      </footer>
    </>
  );
};
